import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { History, Save, RotateCcw, Loader2, Clock, User, ChevronDown, ChevronUp, Plus, Github, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export function VersionHistoryButton({ resourceType, resourceId, resourceName, onRestored }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-lg text-[10px] h-7 px-2.5 gap-1.5" data-testid="version-history-btn">
        <History className="w-3 h-3" /> Versions
      </Button>
      <VersionHistoryDialog open={open} onOpenChange={setOpen} resourceType={resourceType} resourceId={resourceId} resourceName={resourceName} onRestored={onRestored} />
    </>
  );
}

export function VersionHistoryDialog({ open, onOpenChange, resourceType, resourceId, resourceName, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newDesc, setNewDesc] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (open && resourceType && resourceId) {
      setLoading(true);
      api.getVersions(resourceType, resourceId)
        .then(setVersions)
        .catch(() => setVersions([]))
        .finally(() => setLoading(false));
    }
  }, [open, resourceType, resourceId]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const ver = await api.createVersion(resourceType, resourceId, newDesc);
      setNewDesc('');
      setShowCreate(false);
      // Refresh from server so numbering (built-in) or commit list (GitHub) is accurate.
      const updated = await api.getVersions(resourceType, resourceId);
      setVersions(updated);
      toast.success(ver.source === 'github' ? 'Committed to GitHub' : `Version ${ver.version_number} saved`);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleRestore = async (versionId, versionNum) => {
    setRestoring(versionId);
    try {
      await api.restoreVersion(resourceType, resourceId, versionId);
      toast.success(`Restored to version ${versionNum}`);
      if (onRestored) onRestored();
      // Refresh versions
      const updated = await api.getVersions(resourceType, resourceId);
      setVersions(updated);
    } catch (err) { toast.error(err.message); }
    finally { setRestoring(null); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <History className="w-4 h-4 text-primary" /> Version History
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between mt-1 mb-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{resourceName}</p>
            <p className="text-[10px] text-muted-foreground">{resourceType.replace('_', ' ')} &middot; {versions.length} versions</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowCreate(!showCreate)} className="rounded-lg text-[10px] h-7 gap-1" data-testid="save-version-btn">
            <Plus className="w-3 h-3" /> Save Version
          </Button>
        </div>

        {showCreate && (
          <div className="flex gap-2 mb-3 p-2.5 rounded-xl bg-primary/5 border border-primary/20">
            <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Describe what changed..." className="rounded-lg bg-muted/50 border-border/50 text-xs flex-1 h-8" data-testid="version-desc-input" />
            <Button size="sm" onClick={handleCreate} disabled={saving} className="rounded-lg h-8 text-xs px-3" data-testid="confirm-save-version">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        )}

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No versions saved yet</p>
              <p className="text-[10px] text-muted-foreground/70 mt-1">Click "Save Version" to create your first snapshot</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {versions.map((ver, i) => (
                <div key={ver.version_id} className="rounded-xl border border-border/30 hover:border-border/50 transition-all overflow-hidden" data-testid={`version-${ver.version_id}`}>
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" onClick={() => setExpandedId(expandedId === ver.version_id ? null : ver.version_id)}>
                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex-shrink-0">
                      <span className="text-[10px] font-heading font-bold text-primary">v{ver.version_number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {ver.change_description || `Version ${ver.version_number}`}
                        {i === 0 && <Badge className="text-[7px] ml-1.5 bg-emerald-500/15 text-emerald-400 border-emerald-500/20">latest</Badge>}
                        {ver.source === 'github' && <Badge className="text-[7px] ml-1.5 bg-foreground/10 text-foreground border-border/40"><Github className="w-2 h-2 mr-0.5" />GitHub</Badge>}
                      </p>
                      <div className="flex items-center gap-2 text-[9px] text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {formatDate(ver.created_at)}</span>
                        {ver.created_by && <span className="flex items-center gap-0.5"><User className="w-2.5 h-2.5" /> {ver.created_by}</span>}
                        {ver.source === 'github' && ver.version_id && (
                          <span className="flex items-center gap-1">
                            <code className="font-mono text-[8px] bg-muted/40 px-1 rounded" data-testid={`commit-sha-${ver.version_id}`}>{String(ver.version_id).substring(0, 7)}</code>
                            {ver.commit_url && (
                              <a href={ver.commit_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-primary hover:underline" data-testid={`view-on-github-${ver.version_id}`}>
                                View on GitHub <ExternalLink className="w-2 h-2" />
                              </a>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {i > 0 && (
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleRestore(ver.version_id, ver.version_number); }} disabled={restoring === ver.version_id} className="rounded-lg text-[9px] h-6 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid={`restore-${ver.version_id}`}>
                          {restoring === ver.version_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Restore
                        </Button>
                      )}
                      {expandedId === ver.version_id ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : (ver.snapshot ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : null)}
                    </div>
                  </div>
                  {expandedId === ver.version_id && ver.snapshot && (
                    <div className="px-3 pb-3 border-t border-border/20">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-2 mb-1">Snapshot</p>
                      <pre className="text-[9px] font-mono text-foreground/70 bg-muted/20 rounded-lg p-2 max-h-40 overflow-auto whitespace-pre-wrap">
                        {JSON.stringify(ver.snapshot, null, 2).substring(0, 1000)}{JSON.stringify(ver.snapshot, null, 2).length > 1000 ? '\n...' : ''}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
